// State-Specific Statistics Configuration System
// Provides contextual statistics based on month state

import { UserMonthState, getUserFriendlyStateConfig } from './month-status-utils';
import { ConsolidatedPayrollStats } from '@/components/payroll/PayrollPeriodSelector';

export interface StateSpecificStatistic {
  key: string;
  label: string;
  description: string;
  iconName: string;
  colorClass: string;
  priority: number; // 1 = highest priority
  getValue: (stats: ConsolidatedPayrollStats) => string | number;
  getSubtext: (stats: ConsolidatedPayrollStats) => string;
  getAlert?: (stats: ConsolidatedPayrollStats) => string | null;
}

export interface StateStatisticsConfig {
  userState: UserMonthState;
  primaryMetrics: StateSpecificStatistic[];
  secondaryMetrics: StateSpecificStatistic[];
  alerts: Array<{
    condition: (stats: ConsolidatedPayrollStats) => boolean;
    message: string;
    type: 'info' | 'warning' | 'error' | 'success';
  }>;
  sectionHeaders: string[];
}

/**
 * FUTURE State Statistics (2+ months ahead)
 * Focus: Planning & forecasting
 */
const futureStateConfig: StateStatisticsConfig = {
  userState: 'FUTURE',
  primaryMetrics: [
    {
      key: 'projected_cost',
      label: 'Projected Gross Pay',
      description: 'Estimated gross payroll cost',
      iconName: 'TrendingUp',
      colorClass: 'text-primary',
      priority: 1,
      getValue: (stats) => stats.grossPay.planned || 0,
      getSubtext: (stats) => stats.grossPay.planned ? 'Based on planning' : 'No projection available',
      getAlert: (stats) => stats.grossPay.planned === 0 ? 'Set up planning templates to see projections' : null
    },
    {
      key: 'resource_planning',
      label: 'Employee Coverage',
      description: 'Planned employee participation',
      iconName: 'Users',
      colorClass: 'text-blue-600',
      priority: 2,
      getValue: (stats) => stats.totalEmployees > 0 ? 
        `${stats.employeeCoverage.withShifts}/${stats.totalEmployees}` : '0/0',
      getSubtext: (stats) => {
        if (stats.totalEmployees === 0) return 'No employees';
        const percentage = Math.round((stats.employeeCoverage.withShifts / stats.totalEmployees) * 100);
        return `${percentage}% planned coverage`;
      }
    },
    {
      key: 'planning_hours',
      label: 'Planned Hours',
      description: 'Total scheduled hours',
      iconName: 'Clock',
      colorClass: 'text-green-600',
      priority: 3,
      getValue: (stats) => stats.hours.planned ? `${stats.hours.planned.toFixed(1)}h` : '0h',
      getSubtext: (stats) => {
        if (stats.totalEmployees === 0) return 'No employees';
        const avgHours = (stats.hours.planned || 0) / Math.max(stats.totalEmployees, 1);
        return `${avgHours.toFixed(1)}h avg per employee`;
      }
    },
    {
      key: 'budget_status',
      label: 'Budget Status',
      description: 'Planning vs budget allocation',
      iconName: 'Target',
      colorClass: 'text-primary',
      priority: 4,
      getValue: () => 'N/A',
      getSubtext: () => 'Budget comparison pending',
      getAlert: () => 'Set budget allocation to compare projections'
    }
  ],
  secondaryMetrics: [],
  alerts: [
    {
      condition: (stats) => stats.grossPay.planned === 0 && stats.totalEmployees > 0,
      message: 'Start planning shifts to see cost projections',
      type: 'info'
    },
    {
      condition: (stats) => stats.employeeCoverage.withShifts < stats.totalEmployees * 0.5,
      message: 'Low planning coverage - schedule more employees',
      type: 'warning'
    }
  ],
  sectionHeaders: ['FUTURE PLANNING']
};

/**
 * UPCOMING State Statistics (next month)
 * Focus: Preparation & readiness
 */
const upcomingStateConfig: StateStatisticsConfig = {
  userState: 'UPCOMING',
  primaryMetrics: [
    {
      key: 'schedule_readiness',
      label: 'Schedule Coverage',
      description: 'Percentage of shifts scheduled',
      iconName: 'Calendar',
      colorClass: 'text-blue-600',
      priority: 1,
      getValue: (stats) => {
        const percentage = stats.totalEmployees > 0 ? 
          Math.round((stats.employeeCoverage.withShifts / stats.totalEmployees) * 100) : 0;
        return `${percentage}%`;
      },
      getSubtext: (stats) => {
        const scheduled = stats.employeeCoverage.withShifts;
        const total = stats.totalEmployees;
        return `${scheduled} of ${total} employees scheduled`;
      },
      getAlert: (stats) => {
        const percentage = stats.totalEmployees > 0 ? 
          (stats.employeeCoverage.withShifts / stats.totalEmployees) * 100 : 0;
        if (percentage < 50) return 'Complete scheduling by month-end';
        if (percentage < 80) return 'Nearly ready - finish scheduling';
        return null;
      }
    },
    {
      key: 'projected_costs',
      label: 'Projected Costs',
      description: 'Estimated payroll for next month',
      iconName: 'Euro',
      colorClass: 'text-emerald-600',
      priority: 2,
      getValue: (stats) => stats.grossPay.planned || 0,
      getSubtext: (stats) => stats.grossPay.planned ? 'Ready for next month' : 'Complete scheduling for estimate'
    },
    {
      key: 'readiness_score',
      label: 'Readiness Score',
      description: 'Overall preparation status',
      iconName: 'CheckCircle',
      colorClass: 'text-green-600',
      priority: 3,
      getValue: (stats) => {
        let readiness = 0;
        if (stats.employeeCoverage.withShifts > 0) readiness += 40;
        if (stats.hours.planned && stats.hours.planned > 0) readiness += 30;
        if (stats.complianceIssues.overScheduled === 0) readiness += 15;
        if (stats.complianceIssues.underScheduled === 0) readiness += 15;
        return `${readiness}%`;
      },
      getSubtext: (stats) => {
        const readiness = parseInt(stats.complianceRate?.toString() || '0');
        if (readiness >= 90) return 'Ready to start';
        if (readiness >= 70) return 'Mostly ready';
        return 'Needs setup';
      }
    },
    {
      key: 'compliance_check',
      label: 'Compliance Status',
      description: 'Scheduling compliance preview',
      iconName: 'Shield',
      colorClass: 'text-yellow-600',
      priority: 4,
      getValue: (stats) => `${stats.complianceRate?.toFixed(0) || 0}%`,
      getSubtext: (stats) => {
        const issues = (stats.complianceIssues.overScheduled || 0) + (stats.complianceIssues.underScheduled || 0);
        return issues > 0 ? `${issues} issue(s) detected` : 'No issues detected';
      }
    }
  ],
  secondaryMetrics: [],
  alerts: [
    {
      condition: (stats) => (stats.employeeCoverage.withShifts / stats.totalEmployees) < 0.8,
      message: 'Complete scheduling before month starts',
      type: 'warning'
    },
    {
      condition: (stats) => (stats.complianceIssues.overScheduled || 0) > 0,
      message: 'Review over-scheduled employees to avoid overtime',
      type: 'warning'
    },
    {
      condition: (stats) => (stats.employeeCoverage.withShifts / stats.totalEmployees) >= 0.95,
      message: 'Schedule is complete - ready for next month!',
      type: 'success'
    }
  ],
  sectionHeaders: ['UPCOMING PREPARATION']
};

/**
 * IN_PROGRESS State Statistics (current month)
 * Focus: Real-time tracking & progress
 */
const inProgressStateConfig: StateStatisticsConfig = {
  userState: 'IN_PROGRESS',
  primaryMetrics: [
    {
      key: 'actual_vs_planned',
      label: 'Hours Tracking',
      description: 'Actual vs planned hours',
      iconName: 'Activity',
      colorClass: 'text-green-600',
      priority: 1,
      getValue: (stats) => {
        if (stats.hours.actual) {
          return `${stats.hours.actual.toFixed(1)}h`;
        }
        return stats.hours.planned ? `${stats.hours.planned.toFixed(1)}h` : '0h';
      },
      getSubtext: (stats) => {
        if (stats.hours.actual && stats.hours.planned) {
          const variance = stats.hours.actual - stats.hours.planned;
          const sign = variance > 0 ? '+' : '';
          return `${sign}${variance.toFixed(1)}h vs planned`;
        }
        return stats.hours.actual ? 'Actual hours logged' : 'Planned hours';
      },
      getAlert: (stats) => {
        if (stats.hours.actual && stats.hours.planned) {
          const variance = (stats.hours.actual - stats.hours.planned) / stats.hours.planned;
          if (variance > 0.15) return 'Significantly over planned hours';
          if (variance < -0.15) return 'Significantly under planned hours';
        }
        return null;
      }
    },
    {
      key: 'live_costs',
      label: 'Live Costs',
      description: 'Running payroll totals',
      iconName: 'TrendingUp',
      colorClass: 'text-blue-600',
      priority: 2,
      getValue: (stats) => {
        if (stats.grossPay.actual) {
          return stats.grossPay.actual;
        }
        return stats.grossPay.planned || 0;
      },
      getSubtext: (stats) => {
        if (stats.grossPay.actual && stats.grossPay.planned) {
          const variance = stats.grossPay.actual - stats.grossPay.planned;
          const percentage = (variance / stats.grossPay.planned) * 100;
          const sign = variance > 0 ? '+' : '';
          return `${sign}${percentage.toFixed(1)}% vs planned`;
        }
        return stats.grossPay.actual ? 'Live tracking' : 'Based on schedule';
      }
    },
    {
      key: 'employee_progress',
      label: 'Employee Progress',
      description: 'Time logging completion',
      iconName: 'Users',
      colorClass: 'text-primary',
      priority: 3,
      getValue: (stats) => `${stats.employeeCoverage.withPayroll}/${stats.totalEmployees}`,
      getSubtext: (stats) => {
        const percentage = stats.totalEmployees > 0 ? 
          Math.round((stats.employeeCoverage.withPayroll / stats.totalEmployees) * 100) : 0;
        return `${percentage}% employees logged time`;
      }
    },
    {
      key: 'compliance_live',
      label: 'Live Compliance',
      description: 'Real-time compliance monitoring',
      iconName: 'Shield',
      colorClass: 'text-orange-600',
      priority: 4,
      getValue: (stats) => `${stats.complianceRate?.toFixed(0) || 0}%`,
      getSubtext: (stats) => {
        const issues = (stats.complianceIssues.overScheduled || 0) + (stats.complianceIssues.underScheduled || 0);
        return issues > 0 ? `${issues} active issue(s)` : 'Compliant';
      }
    }
  ],
  secondaryMetrics: [],
  alerts: [
    {
      condition: (stats) => (stats.employeeCoverage.withPayroll / stats.totalEmployees) < 0.5,
      message: 'Many employees haven\'t logged time yet',
      type: 'warning'
    },
    {
      condition: (stats) => {
        if (!stats.grossPay.actual || !stats.grossPay.planned) return false;
        return (stats.grossPay.actual - stats.grossPay.planned) / stats.grossPay.planned > 0.1;
      },
      message: 'Running over budget - review overtime',
      type: 'warning'
    },
    {
      condition: (stats) => (stats.complianceIssues.overScheduled || 0) > 0,
      message: 'Overtime detected - manage hours carefully',
      type: 'error'
    }
  ],
  sectionHeaders: ['IN PROGRESS TRACKING']
};

/**
 * TO_BE_PROCESSED State Statistics (last month)
 * Focus: Processing readiness & workflow
 */
const toBeProcessedStateConfig: StateStatisticsConfig = {
  userState: 'TO_BE_PROCESSED',
  primaryMetrics: [
    {
      key: 'processing_status',
      label: 'Processing Status',
      description: 'Ready for payment processing',
      iconName: 'CreditCard',
      colorClass: 'text-orange-600',
      priority: 1,
      getValue: (stats) => {
        const total = stats.paymentStatus.completed + stats.paymentStatus.pending;
        return total > 0 ? `${stats.paymentStatus.completed}/${total}` : '0/0';
      },
      getSubtext: (stats) => {
        const total = stats.paymentStatus.completed + stats.paymentStatus.pending;
        if (total === 0) return 'No payments to process';
        const percentage = Math.round((stats.paymentStatus.completed / total) * 100);
        return `${percentage}% processed`;
      },
      getAlert: (stats) => {
        const total = stats.paymentStatus.completed + stats.paymentStatus.pending;
        if (total === 0) return 'Generate payroll to begin processing';
        if (stats.paymentStatus.pending > 0) return `${stats.paymentStatus.pending} payments pending`;
        return null;
      }
    },
    {
      key: 'final_amounts',
      label: 'Final Payroll',
      description: 'Total amount to be paid',
      iconName: 'Euro',
      colorClass: 'text-emerald-600',
      priority: 2,
      getValue: (stats) => stats.netPay,
      getSubtext: (stats) => {
        const employees = stats.employeeCoverage.withPayroll;
        if (employees === 0) return 'No payroll generated';
        const avgPay = stats.netPay / employees;
        return `€${avgPay.toFixed(0)} avg per employee`;
      }
    },
    {
      key: 'approval_status',
      label: 'Approval Status',
      description: 'Manager approval workflow',
      iconName: 'CheckCircle',
      colorClass: 'text-blue-600',
      priority: 3,
      getValue: () => 'Pending',
      getSubtext: () => 'Awaiting manager approval',
      getAlert: () => 'Complete approvals before processing'
    },
    {
      key: 'compliance_final',
      label: 'Final Compliance',
      description: 'All requirements verified',
      iconName: 'Shield',
      colorClass: 'text-green-600',
      priority: 4,
      getValue: (stats) => `${stats.complianceRate?.toFixed(0) || 0}%`,
      getSubtext: (stats) => {
        const issues = (stats.complianceIssues.overScheduled || 0) + (stats.complianceIssues.underScheduled || 0);
        return issues === 0 ? 'All checks passed' : `${issues} issue(s) remaining`;
      }
    }
  ],
  secondaryMetrics: [],
  alerts: [
    {
      condition: (stats) => stats.paymentStatus.pending > 0,
      message: 'Review and approve pending payments',
      type: 'warning'
    },
    {
      condition: (stats) => (stats.complianceIssues.overScheduled || 0) > 0,
      message: 'Resolve compliance issues before processing',
      type: 'error'
    },
    {
      condition: (stats) => stats.paymentStatus.completed > 0 && stats.paymentStatus.pending === 0,
      message: 'All approvals complete - ready to process payments!',
      type: 'success'
    }
  ],
  sectionHeaders: ['PROCESSING WORKFLOW']
};

/**
 * PROCESSED State Statistics (historical)
 * Focus: Final results & analysis
 */
const processedStateConfig: StateStatisticsConfig = {
  userState: 'PROCESSED',
  primaryMetrics: [
    {
      key: 'final_results',
      label: 'Final Results',
      description: 'Completed payroll totals',
      iconName: 'Archive',
      colorClass: 'text-gray-600',
      priority: 1,
      getValue: (stats) => stats.netPay,
      getSubtext: (stats) => `${stats.employeeCoverage.withPayroll} employees paid`
    },
    {
      key: 'variance_analysis',
      label: 'Variance Analysis',
      description: 'Actual vs planned comparison',
      iconName: 'BarChart',
      colorClass: 'text-blue-600',
      priority: 2,
      getValue: (stats) => {
        if (stats.grossPay.actual && stats.grossPay.planned) {
          const variance = stats.grossPay.actual - stats.grossPay.planned;
          const percentage = (variance / stats.grossPay.planned) * 100;
          const sign = variance > 0 ? '+' : '';
          return `${sign}${percentage.toFixed(1)}%`;
        }
        return 'N/A';
      },
      getSubtext: (stats) => {
        if (stats.grossPay.actual && stats.grossPay.planned) {
          return stats.grossPay.actual > stats.grossPay.planned ? 'Over planned' : 'Under planned';
        }
        return 'No planning comparison';
      }
    },
    {
      key: 'processing_date',
      label: 'Completed',
      description: 'Processing completion date',
      iconName: 'Calendar',
      colorClass: 'text-gray-600',
      priority: 3,
      getValue: () => new Date().toLocaleDateString(),
      getSubtext: () => 'Processing completed'
    },
    {
      key: 'historical_insights',
      label: 'Performance',
      description: 'Month performance summary',
      iconName: 'TrendingUp',
      colorClass: 'text-primary',
      priority: 4,
      getValue: (stats) => `${stats.complianceRate?.toFixed(0) || 0}%`,
      getSubtext: (stats) => {
        const rate = stats.complianceRate || 0;
        if (rate >= 95) return 'Excellent month';
        if (rate >= 80) return 'Good performance';
        return 'Room for improvement';
      }
    }
  ],
  secondaryMetrics: [],
  alerts: [
    {
      condition: (stats) => (stats.complianceRate || 0) < 80,
      message: 'Review compliance issues for next month',
      type: 'info'
    }
  ],
  sectionHeaders: ['HISTORICAL RESULTS']
};

/**
 * Configuration registry for all month states
 */
export const STATE_STATISTICS_CONFIGS: Record<UserMonthState, StateStatisticsConfig> = {
  FUTURE: futureStateConfig,
  UPCOMING: upcomingStateConfig,
  IN_PROGRESS: inProgressStateConfig,
  TO_BE_PROCESSED: toBeProcessedStateConfig,
  PROCESSED: processedStateConfig
};

/**
 * Gets the appropriate statistics configuration for a given state
 */
export function getStateStatisticsConfig(userState: UserMonthState): StateStatisticsConfig {
  return STATE_STATISTICS_CONFIGS[userState];
}

/**
 * Evaluates all alerts for a given state and statistics
 */
export function evaluateStateAlerts(
  userState: UserMonthState, 
  stats: ConsolidatedPayrollStats
): Array<{message: string; type: 'info' | 'warning' | 'error' | 'success'}> {
  const config = getStateStatisticsConfig(userState);
  
  return config.alerts
    .filter(alert => alert.condition(stats))
    .map(alert => ({
      message: alert.message,
      type: alert.type
    }));
}